import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const KuwaitPage = () => {
  const kuwaitData = {
    name: 'Kuwait',
    region: 'GCC',
    color: '#C09060',
    metaTitle: 'Pharma & Nutraceutical Registration in Kuwait | MOH Kuwait | CTD/eCTD Dossiers',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Kuwait. MOH Kuwait requirements, CTD/eCTD dossier expectations, special food/supplement rules, documents needed and market opportunity in a high-income, import-dependent GCC market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Kuwait',
      subtitle: 'A high-income GCC market with heavy dependence on imported medicines, strong NCD burden and CTD/eCTD-based registration under the Ministry of Health — ideal for premium generics and well-documented supplements.',
      cta: 'Start Your Kuwait Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~4.84M' },
      { label: 'Income Level', value: 'High-income economy' },
      { label: 'Health Expenditure (% of GDP)', value: '~5.8%' },
      { label: 'Pharma Imports (2023)', value: '~USD 1.88B' },
      { label: 'Import Share', value: '~5% of total imports' },
      { label: 'Regulatory Authority', value: 'MOH Kuwait' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Oil-Rich, Insurance-Backed, Import-Heavy',
      stats: [
        'Kuwait imports ~USD 1.88 billion in pharmaceuticals annually',
        'Represents ~5% of all imports',
        'MOH oversees medicines via Drug and Food Control / Pharmaceutical Registration units',
        'Drug registration with MOH mandatory before marketing',
        'Decades of GCC regulatory experience (40+ years)',
        'Health services mix: public hospitals/clinics + strong private sector',
        'Serves Kuwaitis and expatriates with increasing insurance coverage',
        'Almost all medicines are imported'
      ],
      opportunities: [
        'Large pharmaceutical import bill for mid-sized GCC country',
        'Prime target for high-quality, well-positioned products',
        'PAFN oversees food and nutrition with recent modernization',
        'Special foods require pre-registration with Nutrition Unit',
        'eCTD digitalization drive improving efficiency'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: High Obesity, High Diabetes, High Spend',
      subtitle: 'Kuwait faces severe NCD epidemic with obesity and diabetes at very high prevalence, driving structural demand for chronic therapies.',
      categories: [
        {
          name: 'Non-Communicable Diseases — Major Epidemic',
          items: [
            'Raised blood glucose (diabetes): 15% of adults 18+',
            'Raised blood pressure: 18% of adults 18+',
            'Adult obesity: 37% overall (33% men, 44% women) - among highest globally',
            'Four major NCDs cause ~65% of deaths',
            'Nearly 1 in 5 Kuwaitis die from NCD before age 70',
            'Hypertension: 16.1% in population studies',
            'Diabetes: 19% prevalence in adults (higher in older age groups)',
            'Pharma: Antidiabetics, antihypertensives, lipid-lowering, cardiovascular, renal, oncology, respiratory',
            'Nutra: Cardiometabolic support, weight-control, antioxidant, lifestyle-modifying supplements'
          ]
        },
        {
          name: 'Nutrition & Food Policy',
          items: [
            'Kuwait Food-Based Dietary Guidelines (KFBDG) address unhealthy patterns',
            'Promote fruit/vegetable intake, whole grains, reduced sugar/salt',
            'PAFN Resolution No. 5 and 6 of 2023 regulate imported food and nutritional claims',
            'Tightening control over labelling and permissible claims',
            'Pharma: NCD management medicines, acute care for obesity complications, essential generics',
            'Nutra: Products aligned with national dietary guidelines and permitted nutrition/health claims'
          ]
        },
        {
          name: 'Best-Fit Product Categories',
          items: [
            'Pharma: Antidiabetics, antihypertensives, statins, cardiology, respiratory, oncology; high-quality generics with SRA/WHO-GMP',
            'Nutra: Heart & metabolic support, weight-management, vitamins/minerals, paediatric & maternal nutrition per Kuwait food standards'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Ministry of Health, Kuwait (MOH) & Public Authority for Food and Nutrition (PAFN)',
      description: 'MOH via Drug and Food Control / Pharmaceutical Registration units regulates medicines for quality, safety, efficacy, price, patents. PAFN regulates food imports, nutritional claims, school/food policies. MOH introduced eCTD Guidelines making eCTD standard for new drug applications.',
      pharma: {
        timeline: 'Several months from complete submission; complex products or queries may extend timeline',
        requirements: [
          'Foreign manufacturers must use local Kuwaiti agent/distributor',
          'Registration with MOH mandatory before distribution',
          'CTD-compliant dossiers required per Drug & Food Control guidance',
          'eCTD now expected format for many products',
          'Module structure, technical envelope, lifecycle management, sequence numbering',
          'Application form, administrative documents (company registration, power of attorney, agency agreement)',
          'GMP certificate of manufacturing site',
          'CPP or free sale certificate where applicable',
          'Product samples and analytical methods',
          'Structured assessment for quality, safety, efficacy (40+ years GCC experience)',
          'Query process for clarifications or additional data',
          'Price controls, patent issues, renewal via MOH and GCC frameworks'
        ],
        insight: 'Kuwait has 40+ years GCC regulatory experience with well-established system. eCTD now mandatory standard. Complete CTD/eCTD approvals typically take several months but extend with data issues or complex queries.'
      },
      nutra: {
        timeline: 'Depends on pre-registration pathway and PAFN compliance',
        requirements: [
          'All special foods must be pre-registered with Nutrition Unit before import',
          'Categories: diet foods (KS 874/2004), health foods, infant foods (KS 877/1997, 696/1995, 878/1997), artificial sweeteners (KS 1176/1999)',
          'PAFN Ministerial Resolution No. 6 of 2023 sets rules for nutritional claims and labelling',
          'Food/special-food route if: nutrient levels within supplement range, claims nutritional/health-supporting (not disease-treating), form matches Kuwait/GCC food standards',
          'Local importer handles pre-registration with PAFN/Nutrition Unit',
          'Subject to food import controls (labelling, ingredients, contaminants)',
          'If dosage form + therapeutic claims → MOH medicinal registration required'
        ],
        insight: 'Most nutraceuticals treated as special foods requiring pre-registration with Nutrition Unit. PAFN Resolution 6/2023 tightened imported food and nutritional claims control. Clear classification critical to avoid MOH medicinal route.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with MOH Kuwait',
      steps: [
        'Local Agent / Registration Holder - Foreign manufacturers must use local Kuwaiti agent',
        'Registration with MOH mandatory before distribution',
        'Dossier Format CTD/eCTD - MOH requires CTD-compliant dossiers',
        'eCTD now expected format with module structure and lifecycle management',
        'Application Package - Form, administrative documents, agency agreement, power of attorney',
        'CTD dossier: Module 1 (regional/admin), Module 2 (summaries), Module 3 (CMC), Modules 4-5 (non-clinical/clinical)',
        'GMP certificate and CPP/free sale certificate',
        'Product samples and analytical methods',
        'Assessment, Queries & Timelines - Structured review by Drug & Food Control',
        'Query process for clarifications/additional data',
        'Approval with pricing and patent review',
        'Variations filed via eCTD sequences in new digital system'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals & Special Foods in Kuwait',
      steps: [
        'Classification determination - Special food vs medicine',
        'Pre-registration with Nutrition Unit required for all special foods',
        'Categories: diet foods, health foods, infant foods, artificial sweeteners per Kuwait Standards',
        'PAFN Ministerial Resolution No. 6/2023 compliance for imported food',
        'Local importer handles pre-registration process',
        'Full composition and specification sheet (vitamins, minerals, bioactives)',
        'Finished-product COA and microbiological tests',
        'Manufacturer GMP or food-safety certification (ISO 22000/HACCP)',
        'Label artwork (Arabic/English) meeting Kuwait/GCC prepacked food labelling (GSO)',
        'Compliance with imported food & nutritional claims resolution',
        'Nutrient levels within food-supplement range',
        'Claims nutritional/health-supporting (not disease-treating)',
        'Product form matching Kuwait/GCC food standards'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (CTD/eCTD to MOH)': [
        'MOH application form signed by local agent',
        'CTD/eCTD dossier: Module 1 (administrative), Modules 2-5 as applicable',
        'Legal documents: agency agreement, trade licence, power of attorney',
        'GMP certificate and CPP/free-sale certificate',
        'COA, stability data, analytical methods',
        'Packaging/leaflet samples (Arabic/English) with required statements'
      ],
      'Nutraceuticals / Special Foods (PAFN / Nutrition Unit)': [
        'Application for pre-registration of special food with Nutrition Unit',
        'Full composition and specification sheet',
        'Finished-product COA and microbiological tests',
        'Manufacturer GMP or food-safety certification (ISO 22000/HACCP)',
        'Label artwork (Arabic/English) meeting Kuwait/GCC labelling (GSO)',
        'Compliance with imported food & nutritional claims resolution'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Kuwait Should Be in Your GCC Strategy',
      reasons: [
        'High-income, high-spend: health expenditure ~6% of GDP',
        'Strong public and private healthcare sectors',
        'Large pharma import bill (~USD 1.9 billion) with almost no local manufacturing',
        'Structurally import-reliant market',
        'NCD epidemic: obesity 37%, diabetes 19% - cardiometabolic portfolios essential',
        'CTD/eCTD and GCC alignment: MOH transitioned to eCTD',
        'Kuwait participates in GCC regulatory harmonisation',
        'Dossier reuse easier across region',
        'Food & supplement modernisation: PAFN recent resolutions show nutrition focus',
        'Environment for compliant, evidence-backed supplements'
      ],
      bestPortfolio: [
        'Pharma: Antidiabetics, antihypertensives, statins, cardiology, respiratory, oncology; high-quality generics with SRA/WHO-GMP',
        'Nutra: Heart & metabolic support, weight-management, vitamins/minerals, paediatric & maternal nutrition per Kuwait food standards'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Kuwait',
      services: [
        'Portfolio & GCC Cluster Planning - Prioritize SKUs for Kuwait NCD burden and pricing',
        'CTD/eCTD Dossier & Registration - Convert to Kuwait-ready CTD/eCTD sequences',
        'Agency documents and CPP/GMP coordination',
        'Supplement vs Medicine Strategy - Decide special foods via PAFN vs medicines route',
        'Label & Claims Localization - Arabic/English labels aligned with nutritional-claim rules',
        'Lifecycle & Partner Support - Coordinate with Kuwaiti partner on renewals, variations',
        'GCC Reliance Strategy - Leverage harmonisation for regional expansion'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in Kuwait?',
        answer: 'The Ministry of Health regulates medicines via its Drug & Food Control / Pharmaceutical Registration units, which have decades of GCC regulatory experience (40+ years).'
      },
      {
        question: 'What dossier format does Kuwait require?',
        answer: 'Kuwait requires CTD-compliant dossiers, and MOH has introduced eCTD guidelines making eCTD the standard format for new and many existing products.'
      },
      {
        question: 'Is registration mandatory before import?',
        answer: 'Yes. Legal and regulatory sources confirm that registration of pharmaceutical products with MOH is mandatory before distribution or sale.'
      },
      {
        question: 'How are supplements regulated?',
        answer: 'Most nutraceuticals are treated as special foods and must be pre-registered with the Nutrition Unit under Kuwait Standards. They must also comply with PAFN rules on imported food and nutritional claims (Resolution 6/2023).'
      },
      {
        question: 'How long does drug registration take?',
        answer: 'Official documents emphasize requirements rather than fixed timelines. In practice, a well-prepared CTD/eCTD file may complete in several months, but complex products or heavy query cycles can extend this.'
      }
    ]
  };

  return <CountryTemplate countryData={kuwaitData} />;
};
